import { useState } from 'react'
import type { Category, Client, Todo } from '../../shared/types'
import { formatHours, parseDuration } from '../../shared/duration'
import { appToday } from '../datetime'
import CategoryCombo from './CategoryCombo'
import ClientCombo from './ClientCombo'

export interface LogEntryPatch {
  client_id: number | null
  description: string
  category_id: number | null
  entry_date: string
  duration_minutes: number
  start_time: string | null
}

// Completion dialog for a to-do: optionally log a time entry, then mark done.
// Log & complete (creates an entry), Don't log (marks done only), or Cancel.
export default function LogTodoModal({
  todo,
  clients,
  allCats,
  onCancel,
  onLog,
  onDontLog,
}: {
  todo: Todo
  clients: Client[]
  allCats: Category[]
  onCancel: () => void
  onLog: (p: LogEntryPatch) => void
  onDontLog: () => void
}) {
  const [clientId, setClientId] = useState<number | null>(todo.client_id)
  const [description, setDescription] = useState(todo.task)
  const [categoryId, setCategoryId] = useState<number | null>(todo.category_id)
  const [date, setDate] = useState(todo.due_date || appToday())
  const [startTime, setStartTime] = useState(todo.start_time ?? '')
  const [durText, setDurText] = useState(formatHours(todo.est_minutes || 0))
  const cats = clientId != null ? allCats.filter((c) => c.client_id === clientId) : []
  const durMinutes = parseDuration(durText) ?? todo.est_minutes ?? 0

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-auto bg-black/40 p-4 sm:p-6">
      <div className="mt-16 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Complete to-do</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onCancel}>
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Client</span>
            <div className="rounded-md border border-slate-300 px-1">
              <ClientCombo value={clientId} clients={clients} onCommit={(id) => { setClientId(id); setCategoryId(null) }} gridKeyDown={() => {}} registerRef={() => {}} />
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Description</span>
            <input className={fieldCls} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Category</span>
            <div className={'rounded-md border border-slate-300 px-1 ' + (clientId == null ? 'bg-slate-50' : '')}>
              <CategoryCombo value={categoryId} categories={cats} disabled={clientId == null} onCommit={setCategoryId} gridKeyDown={() => {}} registerRef={() => {}} />
            </div>
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Date</span>
              <input type="date" className={fieldCls + ' tabular'} value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Start</span>
              <input type="time" className={fieldCls + ' tabular'} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Duration (h)</span>
              <input className={fieldCls + ' tabular'} value={durText} onChange={(e) => setDurText(e.target.value)} />
            </label>
          </div>
          <p className="text-xs text-slate-400">No start time → the entry lands at the start of the day on the log calendar.</p>
          {clientId == null && <p className="text-xs text-amber-700">Pick a client to log time (or use “Don’t log”).</p>}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <button className="text-sm text-slate-500 hover:text-slate-800" onClick={onCancel}>
            Cancel
          </button>
          <div className="flex gap-2">
            <button className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" onClick={onDontLog}>
              Don’t log
            </button>
            <button
              className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              disabled={clientId == null}
              onClick={() => onLog({ client_id: clientId, description, category_id: categoryId, entry_date: date, duration_minutes: durMinutes, start_time: startTime || null })}
            >
              Log &amp; complete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const fieldCls = 'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[var(--accent)]'
